import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { BookOpen, GraduationCap, ListChecks, Sparkles, Loader2, Quote, Lock, Copy, Check, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { generateOutline, generatePeerReviewSummary, resolveTitle, type PeerReviewSummary, type ResolveTitleResult } from "@/lib/smartessay.functions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Outline } from "@/lib/smartessay-types";
import { useAccess } from "@/hooks/useAccess";
import { useManageSubscription } from "@/hooks/useManageSubscription";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PastDueBanner } from "@/components/PastDueBanner";
import { MicButton } from "@/components/MicButton";
import { SourceDropzone } from "@/components/SourceDropzone";
import { SourceInputTabs, type Mode as SourceMode } from "@/components/SourceInputTabs";
import { EssayPdfButton } from "@/components/EssayPdfButton";
import { PeerReviewView } from "@/components/PeerReviewView";
import { AcademicDisclaimer } from "@/components/AcademicDisclaimer";
import {
  AcademicConsentDialog,
  useAcademicConsent,
} from "@/components/AcademicConsentGate";
import { disclaimerTextCopy } from "@/lib/disclaimers";
import { PrivacyBadge } from "@/components/PrivacyBadge";
import { AgeRuleBanner } from "@/components/AgeRuleBanner";
import { VoiceMatchPanel } from "@/components/VoiceMatchPanel";
import type { VoiceProfile } from "@/lib/voice-style";
import { ParentLockGate } from "@/components/ParentLockGate";
import { SiteHeader } from "@/components/SiteHeader";
import { StepLabel } from "@/components/StepLabel";





type Mode = "essay" | "summary" | "key_concepts";

export const Route = createFileRoute("/generator")({
  head: () => ({
    meta: [
      { title: "Create a new outline — SmartEssay" },
      { name: "description", content: "Build a framed essay outline, summary, or key-concept map from any title or passage. Real cited quotes, sentence frames, and tone controls." },
      { property: "og:title", content: "Create a new outline — SmartEssay" },
      { property: "og:description", content: "Build a framed essay outline, summary, or key-concept map from any title or passage." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AppPage,
});

const MODES: { id: Mode; label: string; desc: string; icon: typeof BookOpen }[] = [
  { id: "essay", label: "Essay", desc: "Answer in CER format.", icon: BookOpen },
  { id: "summary", label: "Summary", desc: "Boil the work down to its main ideas.", icon: ListChecks },
  { id: "key_concepts", label: "Key Concepts", desc: "Map the central themes and ideas.", icon: Sparkles },
];

function extractSourceLabel(text: string): string | null {
  const match = text.match(/^=== Source \d+:\s*(.+?) ===/m);
  return match?.[1]?.trim() || null;
}

function AppPage() {
  const navigate = useNavigate();
  const generate = useServerFn(generateOutline);
  const generatePeerReview = useServerFn(generatePeerReviewSummary);
  const verifyTitle = useServerFn(resolveTitle);


  const [checking, setChecking] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("essay");
  const [title, setTitle] = useState("");
  const [sourceMode, setSourceMode] = useState<SourceMode>("text");
  const [useText, setUseText] = useState(false);
  const [sourceText, setSourceText] = useState("");
  const [prompt, setPrompt] = useState("");
  const [evidenceCount, setEvidenceCount] = useState<number>(1);
  const [evidenceCountInput, setEvidenceCountInput] = useState("1");
  const [level, setLevel] = useState<"intermediate" | "advanced">("intermediate");
  const [paragraphCount, setParagraphCount] = useState<number>(4);
  const [paragraphCountInput, setParagraphCountInput] = useState("4");
  const [wordMin, setWordMin] = useState<number>(500);
  const [wordMax, setWordMax] = useState<number>(800);
  const [wordMinInput, setWordMinInput] = useState("500");
  const [wordMaxInput, setWordMaxInput] = useState("800");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [titleCheck, setTitleCheck] = useState<ResolveTitleResult | null>(null);
  const [pendingTitle, setPendingTitle] = useState<string>("");
  const [outline, setOutline] = useState<Outline | null>(null);
  const [peerReview, setPeerReview] = useState<PeerReviewSummary | null>(null);
  const [peerReviewMode, setPeerReviewMode] = useState(false);
  const [peerReviewTone, setPeerReviewTone] = useState<"academic" | "natural">("academic");

  const [voiceProfile, setVoiceProfile] = useState<VoiceProfile | null>(null);

  type GenArgs = {
    assignment_type: Mode;
    title: string;
    prompt?: string;
    source_text?: string;
    evidence_count: number;
    level: "intermediate" | "advanced";
    tone: "academic" | "natural";
    paragraph_count: number;
    word_min: number;
    word_max: number;
  };
  const [lastArgs, setLastArgs] = useState<GenArgs | null>(null);
  const [relevelLoading, setRelevelLoading] = useState(false);
  const [retoneLoading, setRetoneLoading] = useState(false);
  const access = useAccess(userId);
  const { manage, loading: portalLoading } = useManageSubscription();
  const consent = useAcademicConsent();

  useEffect(() => {
    // reset form on fresh mount
    setMode("essay");
    setTitle("");
    setUseText(false);
    setSourceText("");
    setPrompt("");
    setEvidenceCount(1);
    setEvidenceCountInput("1");
    setLevel("intermediate");
    setParagraphCount(4);
    setParagraphCountInput("4");
    setWordMin(500);
    setWordMax(800);
    setWordMinInput("500");
    setWordMaxInput("800");
    setOutline(null);
    setPeerReview(null);
    setPeerReviewMode(false);
    setPeerReviewTone("academic");
    setLastArgs(null);

    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) navigate({ to: "/auth" });
      else {
        setUserId(data.session.user.id);
        setChecking(false);
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate({ to: "/auth" });
      else setUserId(session.user.id);
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  // Show welcome consent modal on first signed-in visit
  const showWelcome = consent.showWelcome;
  useEffect(() => {
    if (!checking && userId) showWelcome();
  }, [checking, userId, showWelcome]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    consent.requireConsent(() => {
      void onGenerate();
    });
  }

  async function onGenerate() {
    if (!access.hasAccess) {
      navigate({ to: "/pricing" });
      return;
    }

    // Normalize word range so partially-typed values are resolved before generating.
    const rawMin = parseInt(wordMinInput, 10);
    const rawMax = parseInt(wordMaxInput, 10);
    let nextMin = Number.isFinite(rawMin) ? Math.max(100, Math.min(10000, rawMin)) : wordMin;
    let nextMax = Number.isFinite(rawMax) ? Math.max(100, Math.min(10000, rawMax)) : wordMax;
    if (nextMin > nextMax) [nextMin, nextMax] = [nextMax, nextMin];
    const rawEvidence = parseInt(evidenceCountInput, 10);
    const nextEvidenceCount = Number.isFinite(rawEvidence) ? Math.max(1, Math.min(10, rawEvidence)) : evidenceCount;
    const rawParagraphs = parseInt(paragraphCountInput, 10);
    const nextParagraphCount = Number.isFinite(rawParagraphs) ? Math.max(1, Math.min(20, rawParagraphs)) : paragraphCount;
    setWordMin(nextMin);
    setWordMax(nextMax);
    setWordMinInput(String(nextMin));
    setWordMaxInput(String(nextMax));
    setEvidenceCount(nextEvidenceCount);
    setEvidenceCountInput(String(nextEvidenceCount));
    setParagraphCount(nextParagraphCount);
    setParagraphCountInput(String(nextParagraphCount));

    // Peer-reviewed article summary branch
    if (mode === "summary" && peerReviewMode) {
      if (!title.trim() && (!useText || !sourceText.trim())) {
        return toast.error("Enter a title or paste the article text.");
      }
      setLoading(true);
      setOutline(null);
      setPeerReview(null);
      try {
        const result = await generatePeerReview({
          data: {
            title: title.trim() || "Untitled article",
            source_text: useText && sourceText.trim() ? sourceText.trim() : undefined,
            level,
            tone: peerReviewTone,
            evidence_count: nextEvidenceCount,
            word_min: nextMin,
            word_max: nextMax,
            voice_profile: voiceProfile ?? undefined,
          },
        });
        setPeerReview(result);
        access.refresh();
      } catch (err) {
        toast.error((err as Error).message || "Couldn't generate summary.");
      } finally {
        setLoading(false);
      }
      return;
    }


    const hasSource = useText && sourceText.trim().length > 0;

    // Title is optional when the student pasted/uploaded a source. Fall back to a label derived from the source.
    let workingTitle = title.trim() || "Untitled source";
    if (!title.trim() && hasSource) {
      const extracted = extractSourceLabel(sourceText.trim());
      workingTitle = extracted || "Source-based outline";
    }

    if (!title.trim() && !hasSource) return toast.error("Add a title or paste a source first.");
    if (!prompt.trim()) return toast.error("Add a prompt or question to answer.");

    // Verify the title before generating, unless the student pasted source text
    // (in which case we use that text directly and don't need to identify the work).
    if (!hasSource) {
      setVerifying(true);
      try {
        const check = await verifyTitle({ data: { title: workingTitle } });
        if (check.status !== "match") {
          setTitleCheck(check);
          setPendingTitle(workingTitle);
          return; // wait for the user to confirm via the dialog
        }
        // status === "match": if the model returned a canonical title with
        // minor cleanup (capitalization, "The"), use the canonical version.
        if (check.canonical_title && check.canonical_title.trim() && check.canonical_title.trim().toLowerCase() !== workingTitle.toLowerCase()) {
          setTitle(check.canonical_title.trim());
        }
      } catch {
        // Verification failure shouldn't block generation — just continue.
      } finally {
        setVerifying(false);
      }
    }

    await runGenerate(workingTitle, nextMin, nextMax, nextParagraphCount, nextEvidenceCount);
  }

  async function runGenerate(useTitle: string, nextMin: number, nextMax: number, nextParagraphCount = paragraphCount, nextEvidenceCount = evidenceCount) {
    setLoading(true);
    setOutline(null);
    setPeerReview(null);
    const args: GenArgs = {
      assignment_type: mode,
      title: useTitle,
      prompt: prompt.trim(),
      source_text: useText && sourceText.trim() ? sourceText.trim() : undefined,
      evidence_count: nextEvidenceCount,
      level,
      tone: "academic",
      paragraph_count: nextParagraphCount,
      word_min: nextMin,
      word_max: nextMax,
    };
    try {
      const result = await generate({ data: { ...args, voice_profile: voiceProfile ?? undefined } });
      setOutline(result.output as Outline);
      setLastArgs(args);
      access.refresh();
    } catch (err) {
      toast.error((err as Error).message || "Couldn't generate outline.");
    } finally {
      setLoading(false);
    }
  }


  async function changeLevel(newLevel: "intermediate" | "advanced") {
    if (!lastArgs || newLevel === lastArgs.level) return;
    if (!access.hasAccess) {
      navigate({ to: "/pricing" });
      return;
    }
    setRelevelLoading(true);
    const nextArgs: GenArgs = { ...lastArgs, level: newLevel };
    try {
      const result = await generate({ data: { ...nextArgs, voice_profile: voiceProfile ?? undefined } });
      setOutline(result.output as Outline);
      setLastArgs(nextArgs);
      setLevel(newLevel);
      access.refresh();
    } catch (err) {
      toast.error((err as Error).message || "Couldn't rewrite at that level.");
    } finally {
      setRelevelLoading(false);
    }
  }

  async function changeTone(newTone: "academic" | "natural") {
    if (!lastArgs || newTone === lastArgs.tone) return;
    if (!access.hasAccess) {
      navigate({ to: "/pricing" });
      return;
    }
    setRetoneLoading(true);
    const nextArgs: GenArgs = { ...lastArgs, tone: newTone };
    try {
      const result = await generate({ data: { ...nextArgs, voice_profile: voiceProfile ?? undefined } });
      setOutline(result.output as Outline);
      setLastArgs(nextArgs);
      access.refresh();
    } catch (err) {
      toast.error((err as Error).message || "Couldn't rewrite in that tone.");
    } finally {
      setRetoneLoading(false);
    }
  }



  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/" });
  }

  if (checking) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;

  return (
    <ParentLockGate userId={userId} toolName="Generator">
    <div className="min-h-screen bg-hero-gradient">
      <PaymentTestModeBanner />
      <PastDueBanner show={access.isPastDue} />
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 pb-24 pt-6">
        <AgeRuleBanner />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">New outline</h1>
            <p className="mt-1 text-muted-foreground">A few choices and we'll frame the rest.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (!window.confirm("Start over? This will clear every field on this page.")) return;
                setTitle("");
                setUseText(false);
                setSourceText("");
                setPrompt("");
                setEvidenceCount(1);
                setEvidenceCountInput("1");
                setLevel("intermediate");
                setParagraphCount(4);
                setParagraphCountInput("4");
                setWordMin(500);
                setWordMax(800);
                setWordMinInput("500");
                setWordMaxInput("800");
                setOutline(null);
                setPeerReview(null);
                setPeerReviewMode(false);
                setTitleCheck(null);
                setPendingTitle("");
                setLastArgs(null);
                toast.success("Cleared — ready for a new outline.");
                setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
              }}
              className="gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Start over
            </Button>
            <PrivacyBadge className="mt-0" />
          </div>
        </div>


        {!access.loading && !access.isSubscribed && (
          <div className={`mt-6 flex items-center justify-between gap-4 rounded-xl border p-4 ${
            access.hasAccess ? "border-border bg-card" : "border-primary bg-primary/5"
          }`}>
            <div className="flex items-start gap-3">
              <Lock className="mt-0.5 h-5 w-5 text-primary" />
              <div className="text-sm">
                <div className="font-medium">
                  {access.hasAccess
                    ? `Free outline available (${access.freeQuota - access.generationCount} of ${access.freeQuota} left)`
                    : "You've used your free outline"}
                </div>
                <div className="text-muted-foreground">
                  {access.hasAccess ? "After this you'll need a plan to keep generating." : "Subscribe to keep generating CER outlines."}
                </div>
              </div>
            </div>
            <Button asChild size="sm" variant={access.hasAccess ? "outline" : "default"}>
              <Link to="/pricing">{access.hasAccess ? "See plans" : "Unlock"}</Link>
            </Button>
          </div>
        )}


        <form onSubmit={handleSubmit} className="mt-8 space-y-7 rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8">
          {/* Step 1: mode */}
          <section>
            <StepLabel n={1}>Assignment type</StepLabel>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {MODES.map(({ id, label, desc, icon: Icon }) => {
                const active = mode === id;
                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() => {
                      setMode(id);
                      setTitle("");
                      setSourceText("");
                      setPrompt("");
                      if (id !== "summary") setPeerReviewMode(false);
                    }}
                    className={`rounded-xl border p-4 text-left transition ${
                      active
                        ? "border-primary bg-primary/5 shadow-glow"
                        : "border-border bg-background hover:border-primary/40"
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="mt-2 font-medium">{label}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Step 2: title */}
          <section className="space-y-1.5">
            <StepLabel n={2} htmlFor="title" hint={useText ? "Optional when you add a source" : undefined}>
              Source title
            </StepLabel>
            <div className="relative">
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={useText ? "e.g. The Great Gatsby (optional if you added a source)" : "e.g. The Great Gatsby"}
                className="pr-12 text-base"
              />
              <MicButton
                label="title"
                value={title}
                onChange={setTitle}
                className="absolute right-2 top-1/2 -translate-y-1/2"
              />
            </div>
          </section>

          {/* Step 3: source */}
          <section>
            <StepLabel n={3}>Source</StepLabel>
            <div className="mt-3 flex gap-2 rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => setUseText(false)}
                className={`flex-1 rounded-md px-3 py-2 text-sm transition ${!useText ? "bg-card shadow-soft" : "text-muted-foreground"}`}
              >
                Use title only
              </button>
              <button
                type="button"
                onClick={() => setUseText(true)}
                className={`flex-1 rounded-md px-3 py-2 text-sm transition ${useText ? "bg-card shadow-soft" : "text-muted-foreground"}`}
              >
                Add a source
              </button>
            </div>
            {useText ? (
              <div>
                {sourceMode !== "audio" && (
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Dictate</span>
                    <MicButton
                      label="article text"
                      value={sourceText}
                      onChange={setSourceText}
                    />
                  </div>
                )}
                <SourceInputTabs value={sourceText} onChange={setSourceText} disabled={!access.loading && !access.hasAccess} lockedMessage="Subscribe to upload more source files." onModeChange={setSourceMode} appendOnUpload />
              </div>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                We'll pull well-known, verifiable quotes from the work. For best accuracy, paste or drop the text.
              </p>
            )}

          </section>

          {/* Peer-reviewed article summary toggle (summary mode only) */}
          {mode === "summary" && (
            <section className="rounded-xl border border-primary/30 bg-primary/5 p-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={peerReviewMode}
                  onChange={(e) => {
                    setPeerReviewMode(e.target.checked);
                  }}
                  className="mt-1 h-4 w-4 accent-primary"
                />
                <div className="text-sm">
                  <div className="font-medium">Peer-reviewed article (APA 7 structured summary)</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Enter the article title or paste the article text to get an APA 7 summary organized into
                    Introduction, Subjects/Participants, Overview of the Measurement Instrument,
                    Results, and Study Critique. Copy/paste-ready for Word or Google Docs, plus PDF.
                  </div>
                </div>
              </label>
            </section>
          )}

          {!peerReviewMode && (
          <section className="space-y-1.5">
            <StepLabel n={4} htmlFor="prompt">
              {mode === "essay" ? "Essay prompt" : mode === "summary" ? "Summary focus / question" : "Key concepts question"}
            </StepLabel>
            <div className="relative">
              <Textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={
                  mode === "essay"
                    ? "Paste the exact prompt from your teacher…"
                    : mode === "summary"
                    ? "What should the summary focus on? e.g. 'Summarize the main argument and how it develops.'"
                    : "What concepts should we map? e.g. 'Explain the key themes of identity and power.'"
                }
                className="min-h-[100px] pr-12"

              />
              <MicButton
                label="prompt"
                value={prompt}
                onChange={setPrompt}
                className="absolute right-2 top-2"
              />
            </div>
            <div className="flex justify-end">
              <span className={`text-xs ${prompt.length > 9000 ? "text-destructive" : prompt.length > 7000 ? "text-yellow-500" : "text-muted-foreground"}`}>
                {prompt.length.toLocaleString()} / 10,000
              </span>
            </div>
          </section>
          )}
          {/* Evidences per claim */}
          <section>
            <StepLabel n={5}>Evidences per claim</StepLabel>
            <div className="mt-3 flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <button
                  key={n}
                  type="button"
                    onClick={() => {
                      setEvidenceCount(n);
                      setEvidenceCountInput(String(n));
                    }}
                  className={`rounded-md border px-3 py-1.5 text-sm transition ${
                    evidenceCount === n
                      ? "border-primary bg-primary/5 text-foreground shadow-soft"
                      : "border-border bg-background text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  {n}
                </button>
              ))}
              <div className="flex items-center gap-2">
                <Label htmlFor="evidence-custom" className="text-xs text-muted-foreground">
                  Custom
                </Label>
                <Input
                  id="evidence-custom"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={evidenceCountInput}
                  onChange={(e) => {
                    const next = e.target.value.replace(/\D/g, "");
                    setEvidenceCountInput(next);
                    const v = parseInt(next, 10);
                    if (Number.isFinite(v)) setEvidenceCount(v);
                  }}
                  onBlur={() => {
                    const v = parseInt(evidenceCountInput, 10);
                    const next = Number.isFinite(v) ? Math.max(1, Math.min(10, v)) : 1;
                    setEvidenceCount(next);
                    setEvidenceCountInput(String(next));
                  }}
                  className="h-9 w-20"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              1–10 evidences per claim.
            </p>
          </section>
          {!peerReviewMode && (
          <section>
            <StepLabel n={6}>Paragraphs</StepLabel>
            <div className="mt-3 flex flex-wrap gap-2">
              {[1, 3, 5, 7, 10, 15].map((n) => (
                <button
                  key={n}
                  type="button"
                    onClick={() => {
                      setParagraphCount(n);
                      setParagraphCountInput(String(n));
                    }}
                  className={`rounded-md border px-3 py-1.5 text-sm transition ${
                    paragraphCount === n
                      ? "border-primary bg-primary/5 text-foreground shadow-soft"
                      : "border-border bg-background text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  {n}
                </button>
              ))}
              <div className="flex items-center gap-2">
                <Label htmlFor="paragraph-custom" className="text-xs text-muted-foreground">
                  Custom
                </Label>
                <Input
                  id="paragraph-custom"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={paragraphCountInput}
                  onChange={(e) => {
                    const next = e.target.value.replace(/\D/g, "");
                    setParagraphCountInput(next);
                    const v = parseInt(next, 10);
                    if (Number.isFinite(v)) setParagraphCount(v);
                  }}
                  onBlur={() => {
                    const v = parseInt(paragraphCountInput, 10);
                    const next = Number.isFinite(v) ? Math.max(1, Math.min(20, v)) : 1;
                    setParagraphCount(next);
                    setParagraphCountInput(String(next));
                  }}
                  className="h-9 w-20"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              1–20 body paragraphs. Use more for graduate-level work.
            </p>
          </section>
          )}

          {/* Word target */}
          <section>
            <StepLabel n={7}>Target length (words)</StepLabel>
            <div className="mt-3 flex flex-wrap gap-2">
                {[
                  { min: 300, max: 500, label: "300–500" },
                  { min: 500, max: 800, label: "500–800" },
                  { min: 800, max: 1200, label: "800–1200" },
                  { min: 1200, max: 1600, label: "1200–1600" },
                  { min: 2000, max: 2500, label: "2000–2500" },
                  { min: 3000, max: 4000, label: "3000–4000" },
                ].map((opt) => {
                  const active = wordMin === opt.min && wordMax === opt.max;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => {
                        setWordMin(opt.min);
                        setWordMax(opt.max);
                        setWordMinInput(String(opt.min));
                        setWordMaxInput(String(opt.max));
                      }}
                      className={`rounded-md border px-3 py-1.5 text-sm transition ${
                        active
                          ? "border-primary bg-primary/5 text-foreground shadow-soft"
                          : "border-border bg-background text-muted-foreground hover:border-primary/40"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div>
                  <Label htmlFor="word-min" className="text-xs text-muted-foreground">Min words</Label>
                  <Input
                    id="word-min"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={wordMinInput}
                    onChange={(e) => setWordMinInput(e.target.value.replace(/\D/g, ""))}
                    onBlur={() => {
                      const v = parseInt(wordMinInput, 10);
                      const clamped = Number.isFinite(v) ? Math.max(100, Math.min(10000, v)) : 100;
                      setWordMin(clamped);
                      setWordMinInput(String(clamped));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const v = parseInt(wordMinInput, 10);
                        const clamped = Number.isFinite(v) ? Math.max(100, Math.min(10000, v)) : 100;
                        setWordMin(clamped);
                        setWordMinInput(String(clamped));
                      }
                    }}
                    className="h-9 w-28"
                  />
                </div>
                <div>
                  <Label htmlFor="word-max" className="text-xs text-muted-foreground">Max words</Label>
                  <Input
                    id="word-max"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={wordMaxInput}
                    onChange={(e) => setWordMaxInput(e.target.value.replace(/\D/g, ""))}
                    onBlur={() => {
                      const v = parseInt(wordMaxInput, 10);
                      const clamped = Number.isFinite(v) ? Math.max(100, Math.min(10000, v)) : 100;
                      setWordMax(clamped);
                      setWordMaxInput(String(clamped));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const v = parseInt(wordMaxInput, 10);
                        const clamped = Number.isFinite(v) ? Math.max(100, Math.min(10000, v)) : 100;
                        setWordMax(clamped);
                        setWordMaxInput(String(clamped));
                      }
                    }}
                    className="h-9 w-28"
                  />
                </div>
              </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Pick a preset or type your own range (100–10,000 words).
            </p>
          </section>



          {!peerReviewMode && (
          <section>
            <StepLabel n={8}>Writing level</StepLabel>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {([
                { id: "intermediate", label: "Intermediate", desc: "Clear and direct." },
                { id: "advanced", label: "Advanced", desc: "Academic vocabulary, analytical phrasing." },
              ] as const).map((opt) => {
                const active = level === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setLevel(opt.id)}
                    className={`rounded-xl border p-4 text-left transition ${
                      active
                        ? "border-primary bg-primary/5 shadow-glow"
                        : "border-border bg-background hover:border-primary/40"
                    }`}
                  >
                    <div className="font-medium">{opt.label}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </section>
          )}

          {/* Peer-review tone toggle */}
          {peerReviewMode && (
            <section>
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Tone
              </Label>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {([
                  { id: "academic" as const, label: "Academic", desc: "Clear, neutral, formal voice." },
                  { id: "natural" as const, label: "Natural", desc: "Warm, real-student voice with varied rhythm." },
                ]).map((opt) => {
                  const active = peerReviewTone === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setPeerReviewTone(opt.id)}
                      className={`rounded-xl border p-4 text-left transition ${
                        active
                          ? "border-primary bg-primary/5 shadow-glow"
                          : "border-border bg-background hover:border-primary/40"
                      }`}
                    >
                      <div className="font-medium">{opt.label}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}



          {/* Voice match */}
          <section>
            <StepLabel n={9}>Match your writing voice</StepLabel>
            <div className="mt-3">
              <VoiceMatchPanel onProfileChange={setVoiceProfile} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Optional. When on, SmartEssay mirrors your sentence rhythm and vocabulary and avoids over-used AI words.
            </p>
          </section>

          <Button type="submit" size="lg" disabled={loading || verifying} className="w-full shadow-glow">
            {verifying ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying the title…</>
            ) : loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {peerReviewMode ? "Summarizing the article…" : "Building your CER outline…"}</>
            ) : !access.hasAccess && !access.loading ? (
              <><Lock className="mr-2 h-4 w-4" /> Unlock to generate</>
            ) : peerReviewMode ? (
              <>Generate APA 7 article summary</>
            ) : (
              <>Generate CER outline</>
            )}
          </Button>
        </form>

        <AlertDialog open={titleCheck !== null} onOpenChange={(o) => { if (!o) setTitleCheck(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {titleCheck?.status === "unknown" ? "We couldn't verify this title" : "Did you mean a different work?"}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm">
                  <div>
                    You typed: <span className="font-medium text-foreground">"{pendingTitle}"</span>
                  </div>
                  {titleCheck?.status === "suggest" && titleCheck.canonical_title && (
                    <div>
                      The closest real work we could find is{" "}
                      <span className="font-medium text-foreground">"{titleCheck.canonical_title}"</span>
                      {titleCheck.author ? <> by {titleCheck.author}</> : null}.
                    </div>
                  )}
                  {titleCheck?.note && (
                    <div className="text-muted-foreground">{titleCheck.note}</div>
                  )}
                  <div className="text-muted-foreground">
                    Pick the correct one so we don't pull quotes from the wrong book. You can also cancel and paste the source text directly for the most accurate citations.
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-2">
              <AlertDialogCancel onClick={() => setTitleCheck(null)}>
                Edit title
              </AlertDialogCancel>
              {titleCheck?.status === "suggest" && titleCheck.canonical_title && (
                <AlertDialogAction
                  onClick={() => {
                    const canonical = titleCheck.canonical_title!.trim();
                    setTitle(canonical);
                    setTitleCheck(null);
                    void runGenerate(canonical, wordMin, wordMax);
                  }}
                >
                  Use "{titleCheck.canonical_title}"
                </AlertDialogAction>
              )}
              <AlertDialogAction
                onClick={() => {
                  const t = pendingTitle;
                  setTitleCheck(null);
                  void runGenerate(t, wordMin, wordMax);
                }}
              >
                Use what I typed
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>


        {peerReview && <PeerReviewView summary={peerReview} />}
        {outline && (
          <>
            <div className="mt-8">
              <AcademicDisclaimer variant="banner" collapsible />
            </div>
            <EssayPdfButton
              outline={outline}
              title={lastArgs?.title ?? title}
              prompt={lastArgs?.prompt}
              assignmentType={lastArgs?.assignment_type ?? mode}
              level={lastArgs?.level ?? level}
              tone={lastArgs?.tone ?? "academic"}
              wordMin={lastArgs?.word_min ?? wordMin}
              wordMax={lastArgs?.word_max ?? wordMax}
            />
            <OutlineView
              outline={outline}
              currentLevel={lastArgs?.level ?? level}
              onChangeLevel={changeLevel}
              relevelLoading={relevelLoading}
              currentTone={lastArgs?.tone ?? "academic"}
              onChangeTone={changeTone}
              retoneLoading={retoneLoading}
            />
          </>
        )}

      </main>

      <AcademicConsentDialog
        open={consent.open}
        onOpenChange={consent.setOpen}
        onAccept={consent.accept}
      />
      
    </div>
    </ParentLockGate>
  );
}

function OutlineView({
  outline,
  currentLevel,
  onChangeLevel,
  relevelLoading,
  currentTone,
  onChangeTone,
  retoneLoading,
}: {
  outline: Outline;
  currentLevel: "intermediate" | "advanced";
  onChangeLevel: (level: "intermediate" | "advanced") => void;
  relevelLoading: boolean;
  currentTone: "academic" | "natural";
  onChangeTone: (tone: "academic" | "natural") => void;
  retoneLoading: boolean;
}) {
  const busy = relevelLoading || retoneLoading;
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(formatOutlineForDocs(outline));
      setCopied(true);
      toast.success("Outline copied — paste it into Word or Google Docs");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  }
  return (
    <section className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs text-accent-foreground">
          <Sparkles className="h-3.5 w-3.5" /> Your framed outline
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5">
            {copied ? (<><Check className="h-3.5 w-3.5" /> Copied</>) : (<><Copy className="h-3.5 w-3.5" /> Copy for Word / Docs</>)}
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Tone:</span>
            <div className="inline-flex rounded-full border border-border bg-background p-0.5">
              {(["academic", "natural"] as const).map((t) => {
                const active = currentTone === t;
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={busy || active}
                    onClick={() => onChangeTone(t)}
                    title={t === "natural" ? "Rewrite with a natural, real-student voice" : "Clear, neutral academic voice"}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                      active
                        ? "bg-primary text-primary-foreground shadow-glow"
                        : "text-muted-foreground hover:text-foreground"
                    } disabled:cursor-not-allowed`}
                  >
                    {retoneLoading && !active ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : t === "academic" ? (
                      "Academic"
                    ) : (
                      "Natural"
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Vocabulary:</span>
            <div className="inline-flex rounded-full border border-border bg-background p-0.5">
              {(["intermediate", "advanced"] as const).map((lvl) => {
                const active = currentLevel === lvl;
                return (
                  <button
                    key={lvl}
                    type="button"
                    disabled={busy || active}
                    onClick={() => onChangeLevel(lvl)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                      active
                        ? "bg-primary text-primary-foreground shadow-glow"
                        : "text-muted-foreground hover:text-foreground"
                    } disabled:cursor-not-allowed`}
                  >
                    {relevelLoading && !active ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : lvl === "intermediate" ? (
                      "Intermediate"
                    ) : (
                      "Advanced"
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <p className="text-base leading-relaxed text-foreground">{outline.overview}</p>


      <div className="mt-6 space-y-5">
        {outline.sections.map((s, i) => (
          <article key={i} className="rounded-xl border border-border bg-background p-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">CLAIM</span>
              <h2 className="font-display text-base font-semibold leading-snug">
                <span className="mr-2 text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                {s.claim}
              </h2>
            </div>

            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Evidence {s.evidence.length > 1 ? `(${s.evidence.length})` : ""}
              </div>
              <div className="mt-2 space-y-2">
                {s.evidence.map((ev, k) => (
                  <blockquote key={k} className="flex gap-3 rounded-lg bg-muted/60 p-4 text-sm italic text-foreground">
                    <Quote className="h-4 w-4 shrink-0 text-primary" />
                    <div>
                      <p>"{ev.quote}"</p>
                      <p className="mt-2 text-xs not-italic text-muted-foreground">— {ev.citation}</p>
                    </div>
                  </blockquote>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-dashed border-border p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reasoning — your turn</div>
              <p className="mt-1 text-sm leading-relaxed">{s.reasoning_frame}</p>
              {s.reasoning_options?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-medium text-muted-foreground">Ideas to fill the blanks:</div>
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {s.reasoning_options.map((opt, j) => (
                      <li key={j} className="rounded-full bg-accent/60 px-2.5 py-1 text-xs text-accent-foreground">
                        {opt}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="mt-3 text-xs text-muted-foreground">💡 {s.hint}</p>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-5">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Closing frame</div>
        <p className="mt-1 text-sm leading-relaxed">{outline.closing_frame}</p>
        {outline.closing_options?.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {outline.closing_options.map((opt, j) => (
              <li key={j} className="rounded-full bg-background px-2.5 py-1 text-xs text-foreground border border-border">
                {opt}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function formatOutlineForDocs(outline: Outline): string {
  let text = "";
  text += "OVERVIEW\n--------\n" + outline.overview + "\n\n";
  outline.sections.forEach((s, i) => {
    text += `SECTION ${i + 1}\n${"-".repeat(8 + String(i + 1).length)}\n\n`;
    text += `CLAIM:\n${s.claim}\n\n`;
    text += `EVIDENCE:\n`;
    s.evidence.forEach((ev, k) => {
      text += `  [${k + 1}] "${ev.quote}"\n      — ${ev.citation}\n\n`;
    });
    text += `REASONING (fill in the blanks):\n${s.reasoning_frame}\n\n`;
    if (s.reasoning_options?.length > 0) {
      text += `Ideas to fill the blanks:\n`;
      s.reasoning_options.forEach((opt) => { text += `  • ${opt}\n`; });
      text += "\n";
    }
    text += `Hint: ${s.hint}\n\n${"=".repeat(40)}\n\n`;
  });
  text += "CLOSING\n-------\n" + outline.closing_frame + "\n\n";
  if (outline.closing_options?.length > 0) {
    text += "Closing ideas:\n";
    outline.closing_options.forEach((opt) => { text += `  • ${opt}\n`; });
  }
  return text.trim() + disclaimerTextCopy();
}
